import React from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, makeStyles } from '@material-ui/core';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar,
} from 'recharts';
import { analyticsService } from '../../services';
import { RangeValue } from '../../constants/product.constant';
import StatCard from '../../components/analytics/StatCard';
import ChartCard from '../../components/analytics/ChartCard';
import { Loadable, useAnalyticsData } from '../../components/analytics/tabHelpers';
import { fmtNum, fmtPct, fmtDuration, CHART_COLORS } from '../../components/analytics/format';

const useStyles = makeStyles(() => ({
  section: { fontSize: 16, fontWeight: 700, color: '#1f2937', margin: '8px 4px 0' },
}));

const SuperPrepTab: React.FC<{ days: RangeValue }> = ({ days }) => {
  const classes = useStyles();
  const { loading, error, data } = useAnalyticsData<any>(async () => {
    const [prep, attempts, time, feed] = await Promise.all([
      analyticsService.getSuperPrepUsers(days),
      analyticsService.getTestAttempts(days),
      analyticsService.getTimeOnTest(days),
      analyticsService.getFeedEngagement(days),
    ]);
    return { ok: true, data: { prep: prep.data, attempts: attempts.data, time: time.data, feed: feed.data } };
  }, [days]);

  const prep = data?.prep || {};
  const attempts = data?.attempts || {};
  const time = data?.time || {};
  const feed = data?.feed || {};

  const signupSeries = (prep?.signupsStudent?.perDay || []).map((d: any) => ({ date: d._id, signups: d.count }));
  const attemptStatus = (attempts?.byStatus || []).map((s: any) => ({ status: s._id, count: s.count }));
  const attemptSeries = (attempts?.perDay || []).map((d: any) => ({ date: d._id, attempts: d.count }));
  const al = time?.attemptLevel || {};
  const pq = time?.perQuestion || {};
  const feeds = feed?.feeds || {};
  const bookmarks = feed?.bookmarks || {};
  const feedTotals = feeds?.totals || {};

  const perQChart = [
    { label: 'Correct', sec: pq.avgCorrect || 0 },
    { label: 'Incorrect', sec: pq.avgIncorrect || 0 },
    { label: 'Attempted', sec: pq.avgAttempted || 0 },
  ];

  return (
    <Loadable loading={loading} error={error}>
      <Grid container spacing={3}>
        {/* --- Users & onboarding --- */}
        <Grid item xs={12}><Typography className={classes.section}>Users & onboarding</Typography></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Student signups (total)" value={fmtNum(prep?.signupsStudent?.total)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Signups (window)" value={fmtNum(prep?.signupsStudent?.inWindow)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Onboarded (all time)" value={fmtNum(prep?.onboarding?.onboardedAll)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Onboarded (window)" value={fmtNum(prep?.onboarding?.onboardedInWindow)} />
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Student signups per day">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={signupSeries} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="signups" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* --- Test attempts --- */}
        <Grid item xs={12}><Typography className={classes.section}>Test attempts</Typography></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Attempts (window)" value={fmtNum(attempts?.inWindow)} sub={`${fmtNum(attempts?.totalAllTime)} all-time`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Distinct users" value={fmtNum(attempts?.distinctUsers)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Submitted" value={fmtNum(attempts?.submitted)} sub={`${fmtPct(attempts?.submitRate)} submit rate`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Papers used" value={fmtNum(attempts?.distinctPapers)} sub={`of ${fmtNum(attempts?.totalPapers)} available`} />
        </Grid>
        <Grid item xs={12} md={8}>
          <ChartCard title="Attempts per day">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={attemptSeries} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="attempts" stroke={CHART_COLORS[2]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Started vs submitted">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={attemptStatus} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={CHART_COLORS[3]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* --- Time on test --- */}
        <Grid item xs={12}><Typography className={classes.section}>Time on test</Typography></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Avg per attempt" value={fmtDuration(al.avgSec)} sub={`median ${fmtDuration(al.medianSec)}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="p25 / p75" value={`${fmtDuration(al.p25Sec)} / ${fmtDuration(al.p75Sec)}`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Total time" value={fmtDuration(al.totalSec)} sub={`${fmtNum(al.count)} attempts`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Per question (avg)" value={fmtDuration(pq.avgAll)} sub={`${fmtNum(pq.rows)} question rows`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Avg time per question (s)">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={perQChart} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any) => `${Math.round(v)}s`} />
                <Bar dataKey="sec" fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <ChartCard title="By test paper">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paper</TableCell>
                  <TableCell align="right">Attempts</TableCell>
                  <TableCell align="right">Avg time</TableCell>
                  <TableCell align="right">Configured</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(time?.byPaper || []).map((p: any) => (
                  <TableRow key={p.testPaperId}>
                    <TableCell><Typography variant="body2">{p.name || '(unknown)'}</Typography></TableCell>
                    <TableCell align="right">{fmtNum(p.attempts)}</TableCell>
                    <TableCell align="right">{fmtDuration(p.avgSec)}</TableCell>
                    <TableCell align="right">{p.durationConfigured ? `${p.durationConfigured}m` : '—'}</TableCell>
                  </TableRow>
                ))}
                {(!time?.byPaper || time.byPaper.length === 0) && (
                  <TableRow><TableCell colSpan={4}><Typography variant="body2" color="textSecondary">No attempts in range</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ChartCard>
        </Grid>

        {/* --- Feed engagement --- */}
        <Grid item xs={12}><Typography className={classes.section}>Feed engagement</Typography></Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Views" value={fmtNum(feedTotals.views)} sub={`${fmtNum(feeds.total)} feeds`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Claps" value={fmtNum(feedTotals.claps)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Interactions" value={fmtNum(feedTotals.interactions)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Saved feeds" value={fmtNum(bookmarks.total)} sub={`${fmtNum(bookmarks.distinctUsers)} users (window)`} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Top feeds by claps">
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Feed</TableCell><TableCell align="right">Claps</TableCell><TableCell align="right">Views</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(feeds?.topByClaps || []).map((f: any) => (
                  <TableRow key={f._id}>
                    <TableCell><Typography variant="body2" noWrap style={{ maxWidth: 280 }}>{f.question}</Typography></TableCell>
                    <TableCell align="right">{fmtNum(f.claps)}</TableCell>
                    <TableCell align="right">{fmtNum(f.views)}</TableCell>
                  </TableRow>
                ))}
                {(!feeds?.topByClaps || feeds.topByClaps.length === 0) && (
                  <TableRow><TableCell colSpan={3}><Typography variant="body2" color="textSecondary">No feeds</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Most saved feeds">
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Feed</TableCell><TableCell align="right">Saves</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(bookmarks?.topFeeds || []).map((f: any) => (
                  <TableRow key={f.feedId}>
                    <TableCell><Typography variant="body2" noWrap style={{ maxWidth: 320 }}>{f.question || '(unknown)'}</Typography></TableCell>
                    <TableCell align="right">{fmtNum(f.saves)}</TableCell>
                  </TableRow>
                ))}
                {(!bookmarks?.topFeeds || bookmarks.topFeeds.length === 0) && (
                  <TableRow><TableCell colSpan={2}><Typography variant="body2" color="textSecondary">No saved feeds</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </ChartCard>
        </Grid>
      </Grid>
    </Loadable>
  );
};

export default SuperPrepTab;
